import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/screens/InteractionDetails.css';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { 
  InteractionResponse, 
  TranscriptResponse, 
  EvaluationSection, 
  EvaluationQuestion as EvaluationQuestionType, 
  SwotItem 
} from '../types/api';
import PlaybackPanelGlobal from '../components/PlaybackPanelGlobal';
import SpeechItem from '../components/SpeechItem';
import EvaluationQuestion from '../components/EvaluationQuestion';
import EvaluationPanel from '../components/EvaluationPanel';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateUtils';
import { useLayout } from '../contexts/LayoutContext';
import NavigationTabs, { NavigationTabsProps } from '../components/common/NavigationTabs';

const InteractionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { updateHeaderProps } = useLayout();
  const [activeTab, setActiveTab] = useState('transcript');
  const [interactionData, setInteractionData] = useState<InteractionResponse | null>(null);
  const [transcriptData, setTranscriptData] = useState<TranscriptResponse | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentTimePosition, setCurrentTimePosition] = useState(0);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [highlightedTexts, setHighlightedTexts] = useState<string[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matchPositions, setMatchPositions] = useState<{id: string, index: number}[]>([]);
  
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const searchStateRef = useRef({
    currentMatchIndex: 0,
    searchText: ''
  });
  
  // Add this ref to track if we've already updated the header
  const headerUpdatedRef = useRef(false);
  
  // Add this near the other state definitions
  const audioRef = useRef<HTMLAudioElement>(null);
  
  useEffect(() => {
    const loadData = async () => {
      if (id) {
        const interactionUrl = api(PATH_SEGMENTS.INTERACTION, id);
        const interaction = await fetchData<InteractionResponse>(interactionUrl);
        setInteractionData(interaction);

        if (interaction.transcript_id) {
          const transcriptUrl = api(PATH_SEGMENTS.TRANSCRIPT, interaction.transcript_id.toString());
          const transcript = await fetchData<TranscriptResponse>(transcriptUrl);
          setTranscriptData(transcript);
        }
      }
    };

    loadData();
  }, [id]);

  // Replace the header update effect with this useLayoutEffect
  useLayoutEffect(() => {
    // Only update header props once when data is available
    if (interactionData && !headerUpdatedRef.current) {
      headerUpdatedRef.current = true;
      
      const externalId = interactionData.interaction.external_id;
      const hasValidExternalId = externalId !== undefined && externalId !== "None";
      const firstName = interactionData.interaction.agent_first_name || '';
      const lastName = interactionData.interaction.agent_last_name || '';
      const time = interactionData.interaction.time || '';
      
      // Schedule this update outside of the React render cycle
      setTimeout(() => {
        updateHeaderProps({
          pageTitle: `Interaction of ${firstName + ' ' + lastName || 'Unknown'} on ${formatDate(time)}`,
          interactionExId: `${externalId || ''}`,
          showInteractionExId: hasValidExternalId,
        });
      }, 0);
    }
  }, [interactionData, updateHeaderProps]);

  // Extract moments from transcript data
  const moments = React.useMemo(() => {
    if (!transcriptData) return [];
    
    const allMoments: { id: string; name: string; time: number }[] = [];
    
    transcriptData.transcript.speech_texts.forEach(speech => {
      speech.transcript_mmts?.forEach(moment => {
        allMoments.push({
          id: `moment-${moment.id}`,
          name: moment.name,
          time: speech.start_ms
        });
      });
    });
    
    return allMoments;
  }, [transcriptData]);

  // Prepare transcript timestamps for the playback panel
  const transcriptTimestamps = React.useMemo(() => {
    return transcriptData?.transcript.speech_texts.map(speech => ({
      id: (speech.id || `speech-${speech.start_ms}`).toString(),
      startTime: speech.start_ms,
      endTime: speech.end_ms || speech.start_ms + 10000, // Fallback if end time is not available
      speaker: {
        id: speech.speaker_id,
        name: speech.speaker_name,
        description: speech.speaker_description
      },
      speech: speech.speech
    })) || [];
  }, [transcriptData]);
  
  // Handle search with stable callback
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    // Update ref to track current search text
    searchStateRef.current.searchText = text;
  }, []);
  
  // Navigate to a specific match
  const navigateToMatch = useCallback((index: number) => {
    if (matchPositions.length === 0) return;
    
    // Ensure index is within bounds
    const boundedIndex = Math.max(0, Math.min(index, matchPositions.length - 1));
    
    // Update ref first to avoid re-render cycles
    searchStateRef.current.currentMatchIndex = boundedIndex;
    setCurrentMatchIndex(boundedIndex);
    
    // Get the speech element to scroll to
    const matchPosition = matchPositions[boundedIndex];
    if (matchPosition && transcriptContainerRef.current) {
      const element = document.getElementById(matchPosition.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setSelectedTranscriptId(matchPosition.id);
      }
    }
  }, [matchPositions]);
  
  // Go to next search match
  const goToNextMatch = useCallback(() => {
    const nextIndex = (currentMatchIndex + 1) % matchPositions.length;
    navigateToMatch(nextIndex);
  }, [currentMatchIndex, matchPositions.length, navigateToMatch]);
  
  // Go to previous search match
  const goToPreviousMatch = useCallback(() => {
    const prevIndex = (currentMatchIndex - 1 + matchPositions.length) % matchPositions.length;
    navigateToMatch(prevIndex);
  }, [currentMatchIndex, matchPositions.length, navigateToMatch]);
  
  // Handle search highlighting
  useEffect(() => {
    if (!transcriptData) return;
    
    if (searchText) {
      try {
        const searchRegex = new RegExp(searchText, 'gi');
        const highlights: string[] = [];
        const positions: Array<{id: string, index: number}> = [];
        
        transcriptData.transcript.speech_texts.forEach(speech => {
          let match;
          const speechId = (speech.id || `speech-${speech.start_ms}`).toString();
          
          while ((match = searchRegex.exec(speech.speech)) !== null) {
            highlights.push(match[0]);
            positions.push({
              id: speechId,
              index: match.index
            });
          }
        });
       
        setHighlightedTexts(highlights);
        setMatchPositions(positions);
        
        // Reset search index if needed without causing a loop
        if (positions.length > 0 && currentMatchIndex >= positions.length) {
          searchStateRef.current.currentMatchIndex = 0;
          setCurrentMatchIndex(0);
        }
      } catch (e) {
        // Handle invalid regex
        console.error("Invalid search pattern:", e);
      }
    } else {
      setHighlightedTexts([]);
      setMatchPositions([]);
      searchStateRef.current.currentMatchIndex = 0;
      setCurrentMatchIndex(0);
    }
  }, [searchText, transcriptData, currentMatchIndex]);
  
  // Handle when playback time is updated
  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTimePosition(time);
    
    // Find the corresponding transcript segment based on time
    const segment = transcriptTimestamps.find(
      item => time >= item.startTime && time <= item.endTime
    );
    
    if (segment) {
      setSelectedTranscriptId(segment.id);
      console.log("handleTimeUpdate: segment id", segment.id);

      // Force transcript to scroll to this item
      // const speechElement = document.getElementById(`speech-${segment.id}`);
      const speechElement = document.getElementById(`${segment.id}`);
      console.log("handleTimeUpdate: speechElement", speechElement);
      if (speechElement) {
        speechElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      else {
        console.log("handleTimeUpdate: speechElement not found at", time);
      }
    }
    else {
      console.log("handleTimeUpdate: segment not found at", time);
    }
  }, [transcriptTimestamps]);
  
  // Handle when playback identifies a different segment is playing
  const handlePlayingSegmentChange = useCallback((segmentId: string) => {
    setActiveSegmentId(segmentId);
    // Auto-scroll to the segment in transcript
    const element = document.getElementById(segmentId);
    if (element && transcriptContainerRef.current) {
      const containerRect = transcriptContainerRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, []);
  
  const handleTranscriptMouseEnter = (speechId: string) => {
    console.log("handleTranscriptMouseEnter: speechId", speechId);
  }

  // Handle when a transcript segment is clicked
  const handleTranscriptClick = (speechId: string) => {
    setSelectedTranscriptId(speechId);
    // transcriptData?.transcript.speech_texts.find(speech => speech.id === speechId)?.start_ms

    const startTime = transcriptTimestamps.find(
      item => item.id === speechId)?.startTime;
    console.log("handleTranscriptClick: startTime", startTime);
    if (startTime) {
      console.log("handleTranscriptClick: audioRef.current.currentTime", audioRef.current?.currentTime);
      if (audioRef.current) {
        audioRef.current.currentTime = startTime/1000;
      }
    }

    // if (audioRef.current) {
    //           audioRef.current.currentTime = time;
    // }
  };
  
  // Update highlightText to use the ref for stable rendering
  const highlightText = useCallback((text: string, speechId: string) => {
    if (!searchText) return text;
    
    const parts = text.split(new RegExp(`(${searchText})`, 'gi'));
    let matchIndex = 0;
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchText.toLowerCase()) {
        // Use the ref value for comparison
        const isCurrentMatch = matchPositions.length > 0 && 
          searchStateRef.current.currentMatchIndex >= 0 && 
          matchPositions[searchStateRef.current.currentMatchIndex]?.id === speechId &&
          matchPositions[searchStateRef.current.currentMatchIndex]?.index === matchIndex;
        
        matchIndex++;
        
        return (
          <mark 
            key={index} 
            className={isCurrentMatch ? 'current-match' : ''}
          >
            {part}
          </mark>
        );
      }
      
      return part;
    });
  }, [searchText, matchPositions]);

  useEffect(() => {
    if (audioRef.current && id) {
      audioRef.current.src = api(PATH_SEGMENTS.AUDIO, id);
    }
  }, [id]);

  if (!interactionData || !transcriptData) {
    return <div className="loading">Loading interaction data...</div>;
  }

  // Calculate total duration from the transcript or fallback to interaction data
  const totalDuration = interactionData.interaction.duration * 1000 || 
    Math.max(...transcriptData.transcript.speech_texts.map(s => s.end_ms || 0));

  // Define the tabs
  const tabItems = [
    {
      id: 'transcript',
      label: 'Transcript',
      icon: 'article',
      searchable: true
    },
    {
      id: 'moments',
      label: 'Moments',
      icon: 'article',
      searchable: true
    },
    {
      id: 'KPI',
      label: 'KPI',
      icon: 'article'
    },
    {
      id: 'summary',
      label: 'Summary',
      icon: 'article',
      searchable: true
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: 'article',
      searchable: true
    },
    
  ];

  const handleScrollSpeechChange = (startTime: number) => {
    // Only update if the time actually changed significantly (to prevent loops)
    if (Math.abs(startTime - currentTimePosition) > 0.5) {
      console.log("Scroll-based seek to:", startTime);
      setCurrentTimePosition(startTime);
      
      // Update audio position
      if (audioRef.current) {
        audioRef.current.currentTime = startTime;
      }
    }
  };

  // Add this function near handleScrollSpeechChange in InteractionDetails.tsx
  const handleTranscriptScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // console.log("handleTranscriptScroll: e", e);
    // Get the container
    const container = e.currentTarget;
    
    // Find visible speech elements
    const speechElements = Array.from(container.querySelectorAll('.speech-item'));
    
    // Find the one most visible in the viewport
    const containerRect = container.getBoundingClientRect();
    const visibleElements = speechElements.filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;
    });
    
    if (visibleElements.length > 0) {
      // Use the middle element if multiple are visible
      const middleElement = visibleElements[Math.floor(visibleElements.length / 2)];
      const startMs = parseInt(middleElement.getAttribute('data-start-ms') || '0', 10);
      
      // Call the time updater with the timestamp (in seconds)
      handleScrollSpeechChange(startMs / 1000);
    }
  };

  return (
    <div className="interaction-details">
      <div className="interaction-content">
        <div className="interaction-main-content">
          <div className="left-column">
            <NavigationTabs
              tabs={tabItems}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              searchValue={searchText}
              onSearch={handleSearch}
              onNavigateNext={goToNextMatch}
              onNavigatePrevious={goToPreviousMatch}
              searchStats={
                highlightedTexts.length > 0 
                  ? {
                      currentMatch: currentMatchIndex + 1,
                      totalMatches: highlightedTexts.length
                    }
                  : undefined
              }
            />
            
            <div className="transcript-section">
              <PlaybackPanelGlobal 
                audioRef={audioRef}
                audioSrc={api(PATH_SEGMENTS.AUDIO, id || "0")}
                currentTranscriptId={selectedTranscriptId || undefined}
                onTimeUpdate={handleTimeUpdate}
                onPlayingSegmentChange={handlePlayingSegmentChange}
                transcriptTimestamps={transcriptTimestamps}
                moments={moments}
                totalDuration={totalDuration}
              />
              
              <div className="transcript-container" onScroll={handleTranscriptScroll}>
                <div className="transcript-content" ref={transcriptContainerRef}>
                  {transcriptData.transcript.speech_texts.map((speech, index) => {
                    const speechId = (speech.id || `speech-${speech.start_ms}`).toString();
                    const isActive = activeSegmentId === speechId;
                    const speaker = {
                      id: speech.speaker_id,
                      name: speech.speaker_name,
                      description: speech.speaker_description
                    }
                    return (
                      <SpeechItem
                        key={index}
                        id={speechId}
                        startMs={speech.start_ms}
                        endMs={speech.end_ms}
                        speech={highlightText(speech.speech, speechId)}
                        speaker={speaker}
                        moments={speech.transcript_mmts}
                        isActive={isActive}
                        transcriptId={speech.transcript_id || (interactionData?.transcript_id || 0)}
                        onClick={handleTranscriptClick}
                        onMouseEnter={handleTranscriptMouseEnter}
                        remark={speech.remark || ''}
                        reviewer={speech.reviewer || ''}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          
          <div className="right-column">
            {transcriptData.evaluation && (
              <EvaluationPanel 
                evaluation={transcriptData.evaluation} 
                onTimeUpdate={handleTimeUpdate}
                // onTimeUpdate={(time) => {
                //   // This will update the audio player position and
                //   // indirectly cause the transcript to scroll
                //   if (audioRef.current) {
                //     audioRef.current.currentTime = time;
                //   }
                // }}
              />
            )}
          </div>
        </div>
      </div>
      <audio ref={audioRef} style={{display: 'none'}} />
    </div>
  );
};

export default InteractionDetails; 